import {Injectable, isDevMode} from '@angular/core';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {Observable, firstValueFrom, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {Task} from "../modules/task/models/task.model";
import {EmailOptions, EmailService} from "../modules/shared/email.service";
import {MessagingService} from "./messaging.service";
import {environment} from "../../environments/environment";
/**
 * Interface representing a notification object.
 */
export interface NotificationModel {
  id: string;
  email: string;  // Changed from userId
  message: string;
  time: Date;
  icon: string;
  color: string;
  status: 'msg-unread' | 'msg-read';
  type?: string;
  link?: string;


}
/**
 * Service for managing notifications in the application.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  /**
   * Constructor for NotificationService.
   * @param firestore - AngularFirestore instance for database operations.
   * @param messagingService - Service for handling messaging functionality
   * @param mailService - Service for handling email operations
   */
  constructor(private firestore: AngularFirestore,
              private messagingService: MessagingService,
              private readonly mailService: EmailService) {
  }
  /**
   * Retrieves notifications for a specific user by email.
   * @param email - Email address of the user.
   * @returns Observable emitting an array of notifications.
   */
  getUserNotifications(email: string): Observable<NotificationModel[]> {
    return this.firestore
      .collection<NotificationModel>(this.env.notifications, ref =>
        ref.where('email', '==', email)  // Changed from userId
          .orderBy('time', 'desc')
          .limit(50)
      )
      .get() // Use get() for a one-time fetch
      .pipe(
        map(snapshot => snapshot.docs.map(doc => {
          const data = doc.data() as NotificationModel;
          const id = doc.id;
          return {...data, id};
        })),
        catchError(error => {
          console.error('Error fetching notifications:', error);
          return of([]); // Return an empty array on error
        }),
        shareReplay(1) // Cache the result
      );
  }



  /**
   * Marks a specific notification as read.
   * @param notificationId - ID of the notification to mark as read.
   * @returns Promise resolving when the operation is complete.
   */
  markAsRead(notificationId: string): Promise<void> {
    return this.firestore
      .collection(this.env.notifications)
      .doc(notificationId)
      .update({status: 'msg-read'});
  }

  /**
   * Marks all unread notifications for a user as read.
   * @param email - Email address of the user.
   * @returns Promise resolving when the operation is complete.
   */
  markAllAsRead(email: string): Promise<void> {  // Changed from userId
    return this.firestore.firestore.runTransaction(async (transaction) => {
      const unreadQuery = await this.firestore
        .collection(this.env.notifications)
        .ref.where('email', '==', email)
        .where('status', '==', 'msg-unread')
        .get();

      unreadQuery.docs.forEach((doc) => {
        transaction.update(doc.ref, {status: 'msg-read'});
      });
    });
  }

  /**
   * Deletes a specific notification.
   * @param notificationId - ID of the notification to delete.
   * @returns Promise resolving when the operation is complete.
   */
  deleteNotification(notificationId: string): Promise<void> {
    return this.firestore
      .collection(this.env.notifications)
      .doc(notificationId)
      .delete();
  }

  /**
   * Creates notifications based on a task and sends them to the assigned users.
   * @param task - Partial task object containing task details.
   * @param config
   */
  createNotificationFromTask(
    task: Partial<Task>,
    config?:Partial<NotificationModel>,
  ): Promise<void> {
    console.log('Creating Notifications from task');
    if (!task) {
      console.error('No task provided to createNotificationFromTask');
      return Promise.resolve();
    }

    // Use a Set to ensure unique emails
    const uniqueEmails = new Set<string>();

    // Add assignees' emails
    (task.assignees || []).forEach(assignee => {
      if (assignee?.email?.trim()) {
        uniqueEmails.add(assignee.email.trim());
      }
    });

    // Add collaborators' emails
    (task.collaborators || []).forEach(collab => {
      if (collab?.email?.trim()) {
        uniqueEmails.add(collab.email.trim());
      }
    });

    // Convert emails back to array and create notifications
    const uniqueRecipients = Array.from(uniqueEmails).map(email => ({
      email,
      userName: this.getUserDisplayName(email)
    }));

    if (uniqueRecipients.length === 0) {
      console.warn('No valid recipients found for task notification');
      return Promise.resolve();
    }

    // Create individual notifications in database
    const notificationPromises = uniqueRecipients.map(recipient => {
      const notification: Omit<NotificationModel, 'id'> = {
        email: recipient.email,
        message: task?.title ?? "New Task Assigned",
        color: config?.color || 'blue',
        icon: config?.icon || 'message',
        link: config?.link || '/home/tasks',
        time: new Date(),
        status: 'msg-unread',
        type: config?.type || 'task',
      };

      return this.createNotification(notification)
        .then(() => console.log('Notification created successfully for:', recipient.email))
        .catch(error => {
          console.error('Error creating notification:', error);
          throw error;
        });
    });

    // Wait for all notifications to be created before sending email
    return Promise.all(notificationPromises)
      .then(async () => {
        if (uniqueRecipients.length > 0) {
          const emailSubject = `New Task Assigned: ${task.title}`;
          const emailBody = `
            <p>Hello,</p>
            <p>A new task has been assigned to you:</p>
            <ul>
              <li><strong>Task:</strong> ${task.title}</li>
              <li><strong>Note:</strong> ${task.note || 'No note provided'}</li>
              <li><strong>Due Date:</strong> ${task.due_date ? new Date(task.due_date).toLocaleDateString() : 'Not set'}</li>
            </ul>
            <p>Please log in to the portal to view the details.</p>
            <p>Thank you.</p>
          `;

          const emailOptions: EmailOptions = {
            email: Array.from(uniqueEmails),
            subject: emailSubject,
            body: emailBody
          };

          try {
            // Convert Observable to Promise using firstValueFrom
            await firstValueFrom(this.mailService.sendMail(emailOptions));
            console.log('Email sent successfully');
          } catch (error) {
            console.error('Error sending email:', error);
            throw error;
          }
        }
      })
      .catch(error => {
        console.error('Error in notification process:', error);
        throw error;
      });
  }

  private getUserDisplayName(email: string): string {
    // Simple function to get display name from email
    return email.split('@')[0].replace(/[._]/g, ' ');
  }
  /**
   * Creates a new notification in the database.
   * @param notification - Notification object without the ID.
   * @returns Promise resolving when the operation is complete.
   */
  async createNotification(notification: Omit<NotificationModel, 'id'>): Promise<void> {
    try {
      await this.firestore.collection(this.env.notifications).add({
        ...notification,
        time: notification.time || new Date()
      });
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  async bulkDeleteNotifications() {
    const query = this.firestore.collection<NotificationModel>(
      this.env.notifications,
      ref => ref.orderBy('time').where('time', '<', new Date('2026-03-01')).limitToLast(4000)
    );

    try {
      const snapshot = await query.get().toPromise();

      if (snapshot?.empty) {
        console.log('No old notifications to delete.');
        return;
      }

      // Firestore allows up to 500 operations in a single batch.
      const batchSize = 500;
      const batches = [];
      let currentBatch = this.firestore.firestore.batch();
      let operationCount = 0;

      snapshot?.docs.forEach((doc, index) => {
        currentBatch.delete(doc.ref);
        operationCount++;

        // When the batch is full, push it to the array and start a new one.
        if (operationCount === batchSize) {
          batches.push(currentBatch);
          currentBatch = this.firestore.firestore.batch();
          operationCount = 0;
        }
      });

      // Add the last batch if it has any pending operations.
      if (operationCount > 0) {
        batches.push(currentBatch);
      }

      console.log(`Deleting ${snapshot?.size} notifications in ${batches.length} batch(es).`);

      // Commit all the batches.
      await Promise.all(batches.map(batch => batch.commit()));

      console.log('Successfully deleted all old notifications.');

    } catch (error) {
      console.error('Error deleting notifications in batch:', error);
    }
  }

}
